import { DESCENDING } from 'constants/sortDirections';
import { useDynamicHeatmapContext } from 'modules/campaignAnalytics/context';
import { useEffect, useState } from 'react';
import { CSVLink } from 'react-csv';
import styled from 'styled-components';
import { BUTTON_VARIANTS, IconButton } from 'ui/button';
import { EmptyState } from 'ui/emptyState';
import { Export16, Help, Search } from 'ui/icons';
import { NoResult, Overview } from 'ui/illustrations';
import { InputText } from 'ui/inputs';

import { Table } from 'ui/table/Table';
import { ColumnData } from 'ui/tableHeader';
import TableHeader from 'ui/tableHeader/TableHeader';
import { TableRows } from 'ui/tableRows';
import { button, colors, spacing, subtleShadow } from 'ui/theme';
import { Toggle } from 'ui/toggle';
import { Tooltip } from 'ui/tooltip';
import { pxToRem } from 'utils/convert';
import { MetricCell } from './MetricCell';
import { DATA_TEST_DYNAMIC_HEATMAP_TABLE } from './constants';
import {
  getCsvRawTableHeaders,
  getCsvTableHeaders,
  getTableHeaderLabels,
  getTableHeaderTooltipLabels,
  sanitizeCSV,
  sortHeatmapRows,
} from './utils';

import { useAnalyticsBotFiltering } from 'modules/campaignAnalytics/hooks';
import { NUMBER, PERCENTAGE } from 'ui/card/constants';
import {
  CampaignAnalyticsCardHeader,
  CenterText,
  StyledCampaignAnalyticsCard,
  StyledHeatmapThumbnail,
  StyledUILink,
  Cell as TableCell,
} from '../../components/Styled';
import { UrlCell } from './UrlCell';

const EmptyStateWrapper = styled.div`
  width: 100%;
  max-height: 60vh;
  min-height: ${spacing.x24};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TableWrapper = styled.div`
  border-top: ${spacing.single} solid ${colors.grey15};
  overflow: auto;
  min-height: ${spacing.x24};
  > * {
    border-collapse: initial;
  }
`;

const TooltipWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const TooltipTriggerWrapper = styled.div`
  padding-left: ${spacing.quarter};
  display: flex;
  align-items: center;
`;

const StyledTable = styled(Table)`
  display: block;
  width: 100%;
`;

const URL_ONLY_VIEW_GRID = `
  grid-template-columns: minmax(${pxToRem(700)}, 1.5fr) 
                         minmax(${pxToRem(115)}, 1fr) 
                         minmax(${pxToRem(120)}, 1fr) 
                         minmax(${pxToRem(135)}, 1fr);
`;

const FULL_VIEW_GRID = `
  grid-template-columns: minmax(${pxToRem(150)}, 1fr) 
                         minmax(${pxToRem(600)}, 1.5fr) 
                         minmax(${pxToRem(115)}, 1fr) 
                         minmax(${pxToRem(120)}, 1fr) 
                         minmax(${pxToRem(135)}, 1fr);
`;

const URL_ONLY_VIEW_GRID_FILTERED = `
  grid-template-columns: minmax(${pxToRem(510)}, 2.5fr) 
                         minmax(${pxToRem(115)}, 1fr) 
                         minmax(${pxToRem(120)}, 1fr) 
                         minmax(${pxToRem(135)}, 1fr);
`;

const FULL_VIEW_GRID_FILTERED = `
  grid-template-columns: minmax(${pxToRem(150)}, 1fr) 
                         minmax(${pxToRem(400)}, 1.5fr) 
                         minmax(${pxToRem(115)}, 1fr) 
                         minmax(${pxToRem(120)}, 1fr) 
                         minmax(${pxToRem(135)}, 1fr);
`;

const StyledRows = styled(TableRows)`
  display: grid;
  &[data-url-only-view="true"][data-is-analytics-bot-filtering-enabled="false"] {
    ${URL_ONLY_VIEW_GRID}
  }
  &[data-url-only-view="false"][data-is-analytics-bot-filtering-enabled="false"] {
    ${FULL_VIEW_GRID}
  }
  &[data-url-only-view="true"][data-is-analytics-bot-filtering-enabled="true"] {
    ${URL_ONLY_VIEW_GRID_FILTERED}
  }
  &[data-url-only-view="false"][data-is-analytics-bot-filtering-enabled="true"] {
    ${FULL_VIEW_GRID_FILTERED}
  }
  > div {
    padding-right: ${spacing.plusHalf};
  }
  > div:first-child {
    padding-left: ${spacing.plusHalf};
  }
`;

const StyledHeader = styled(TableHeader)`
  display: grid;
  &[data-url-only-view="true"][data-is-analytics-bot-filtering-enabled="false"] {
    ${URL_ONLY_VIEW_GRID}
  }
  &[data-url-only-view="false"][data-is-analytics-bot-filtering-enabled="false"] {
    ${FULL_VIEW_GRID}
  }
  &[data-url-only-view="true"][data-is-analytics-bot-filtering-enabled="true"] {
    ${URL_ONLY_VIEW_GRID_FILTERED}
  }
  &[data-url-only-view="false"][data-is-analytics-bot-filtering-enabled="true"] {
    ${FULL_VIEW_GRID_FILTERED}
  }
  > * {
    border-bottom: ${spacing.single} solid ${colors.grey15};
  }
  > div:first-child {
    padding-left: ${spacing.plusHalf};
  }
`;

const Cell = styled(TableCell)`
  display: grid;
  height: 100%;
`;

const TableActions = styled.div`
  display: flex;
  gap: ${spacing.half};
`;

const StyledInputText = styled(InputText)`
  height: ${pxToRem(36)};
  width: ${pxToRem(231)};
  ${subtleShadow};
`;

const RawDataWrapper = styled.div`
  display: flex;
  gap: ${spacing.half};
  align-items: center;
  ${button};
  padding: ${spacing.quarter} ${spacing.threeQuarter};
  border-radius: ${spacing.quarter};
  cursor: pointer;
`;

export const HeatmapTable = () => {
  const [heatmapSortedBy, setHeatmapSortedBy] = useState('totalCount');
  const [urlOnlyView, setUrlOnlyView] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const { templateUrlData, rawLinkData, isFullscreen, userData } = useDynamicHeatmapContext();
  const { email = '' } = JSON.parse(userData || '{}');
  const isAnalyticsBotFilteringEnabled = useAnalyticsBotFiltering();
  const tableHeaderLabels = getTableHeaderLabels(isAnalyticsBotFilteringEnabled);
  const tableHeaderTooltipLabels = getTableHeaderTooltipLabels(isAnalyticsBotFilteringEnabled);

  const HEATMAP_COLS: ColumnData[] = [
    {
      name: 'Link content',
      value: 'text',
      align: 'flex-start',
      RowCell: ({ row }: { row: any }) => {
        // TODO: Update type once we get actual response
        const { text, img, trackingId = '', contentId } = row;
        const isSnippet = trackingId.split('-')?.[0] !== contentId?.toString();

        if (isSnippet) {
          const [snippetId] = trackingId.split('-');

          return (
            <Cell data-content="snippet">
              <div data-test="container">
                <span data-test="content">
                  {img && <StyledHeatmapThumbnail src={img} alt={'Image for link'} />}
                  {text && <span>{text}</span>}
                  {!img && !text && <span>[dynamic]</span>}
                </span>
                <span data-test="label">Snippet ID:</span>
                <StyledUILink
                  data-test="id"
                  data-url-only-view={urlOnlyView}
                  data-is-analytics-bot-filtering-enabled={isAnalyticsBotFilteringEnabled}
                  href={`/templates/snippet/${snippetId}/edit`}
                  target="_blank"
                >
                  {snippetId}
                </StyledUILink>
              </div>
            </Cell>
          );
        }

        return (
          <Cell>
            <CenterText>
              {img && (
                <span>
                  <StyledHeatmapThumbnail src={img} alt={'Image for link'} />
                </span>
              )}
              <span>{text}</span>
            </CenterText>
          </Cell>
        );
      },
    } as ColumnData,
    {
      name: 'URL',
      value: 'url',
      align: 'flex-start',
      RowCell: ({ row }: { row: any }) => (
        <UrlCell
          urlOnlyView={urlOnlyView}
          url={row.url}
          extendedTrackInfo={row.extendedTrackInfo}
          isAnalyticsBotFilteringEnabled={isAnalyticsBotFilteringEnabled}
        />
      ),
    } as ColumnData,
    {
      name: (
        <TooltipWrapper>
          {tableHeaderLabels.clickRate}
          <Tooltip tooltipContent={tableHeaderTooltipLabels.clickRate}>
            <TooltipTriggerWrapper>
              <Help
                height={16}
                width={16}
                color={
                  heatmapSortedBy === 'uniqueClickRate' ? colors.purple90 : colors.textSecondary
                }
              />
            </TooltipTriggerWrapper>
          </Tooltip>
        </TooltipWrapper>
      ),
      value: 'uniqueClickRate',
      align: 'flex-end',
      RowCell: ({ row }: { row: any }) => {
        return <MetricCell primaryVal={row.uniqueClickRate} valueType={PERCENTAGE} />;
      },
    } as ColumnData,
    {
      name: (
        <TooltipWrapper>
          {tableHeaderLabels.totalClicks}
          <Tooltip tooltipContent={tableHeaderTooltipLabels.totalClicks}>
            <TooltipTriggerWrapper>
              <Help
                height={16}
                width={16}
                color={heatmapSortedBy === 'totalCount' ? colors.purple90 : colors.textSecondary}
              />
            </TooltipTriggerWrapper>
          </Tooltip>
        </TooltipWrapper>
      ),
      value: 'totalCount',
      align: 'flex-end',
      RowCell: ({ row }: { row: any }) => {
        return <MetricCell primaryVal={row.totalCount} valueType={NUMBER} />;
      },
    } as ColumnData,
    {
      name: (
        <TooltipWrapper>
          {tableHeaderLabels.uniqueClicks}
          <Tooltip tooltipContent={tableHeaderTooltipLabels.uniqueClicks}>
            <TooltipTriggerWrapper>
              <Help
                height={16}
                width={16}
                color={heatmapSortedBy === 'uniqueCount' ? colors.purple90 : colors.textSecondary}
              />
            </TooltipTriggerWrapper>
          </Tooltip>
        </TooltipWrapper>
      ),
      value: 'uniqueCount',
      align: 'flex-end',
      RowCell: ({ row }: { row: any }) => {
        return <MetricCell primaryVal={row.uniqueCount} valueType={NUMBER} />;
      },
    } as ColumnData,
  ];

  const RAW_LINK_HEATMAP_COLS = HEATMAP_COLS.slice(1);

  const SELECTED_COLS = urlOnlyView ? RAW_LINK_HEATMAP_COLS : HEATMAP_COLS;
  const [heatmapSortDirection, setHeatmapSortDirection] = useState(DESCENDING);
  const selectedData = urlOnlyView ? rawLinkData : templateUrlData;
  const [sortedHeatmapRows, setSortedHeatmapRows] = useState(
    sortHeatmapRows(selectedData, heatmapSortedBy, heatmapSortDirection),
  );
  const campaignId = sortedHeatmapRows[0]?.campaignId;
  const safeEmail = email.replace(/[^a-zA-Z0-9._-]/g, '_');
  const csvFileName =
    userData !== '{}'
      ? `campaign-${campaignId}-${safeEmail}-heatmap.csv`
      : `campaign-${campaignId}-heatmap.csv`;

  const filterFn = (arr: any[], input: string) => {
    const lowerInput = input?.toLowerCase();
    return arr.filter((el: any) => {
      if (!el.img && !el.text && '[dynamic]'.includes(input.toLocaleLowerCase())) {
        return true;
      }
      return (
        el?.url?.toLowerCase().includes(lowerInput) || el?.text?.toLowerCase().includes(lowerInput)
      );
    });
  };

  useEffect(() => {
    const filteredData = searchInputValue ? filterFn(selectedData, searchInputValue) : selectedData;
    setSortedHeatmapRows(sortHeatmapRows(filteredData, heatmapSortedBy, heatmapSortDirection));
  }, [selectedData, heatmapSortDirection, heatmapSortedBy]);

  const filterTableData = (input: string) => {
    setSearchInputValue(input);
    const sorted = sortHeatmapRows(selectedData, heatmapSortedBy, heatmapSortDirection);
    if (input) {
      setSortedHeatmapRows(filterFn(sorted, input));
    } else {
      setSortedHeatmapRows(sorted);
    }
  };

  return (
    <StyledCampaignAnalyticsCard style={{ display: isFullscreen ? 'none' : 'block' }}>
      <CampaignAnalyticsCardHeader>
        Click summary
        <TableActions>
          <RawDataWrapper
            data-test="click-breakdown-raw-data"
            onClick={() => setUrlOnlyView(!urlOnlyView)}
          >
            Show URL breakdown
            <Toggle
              data-test="click-breakdown-raw-data-toggle"
              isActive={urlOnlyView}
              onClick={() => setUrlOnlyView(!urlOnlyView)}
            />
          </RawDataWrapper>
          <StyledInputText
            autoComplete="off"
            data-test="heatmap-index-search"
            id="heatmap-search-input"
            onChange={filterTableData}
            placeholder={'Search...'}
            Prefix={<Search />}
            value={searchInputValue}
          />
          {sortedHeatmapRows?.length > 0 ? (
            <Tooltip delayDuration={600} tooltipContent={'Export'}>
              <CSVLink
                headers={
                  urlOnlyView
                    ? getCsvRawTableHeaders(isAnalyticsBotFilteringEnabled)
                    : getCsvTableHeaders(isAnalyticsBotFilteringEnabled)
                }
                data={sanitizeCSV(sortedHeatmapRows)}
                filename={csvFileName}
                target="_blank"
              >
                <IconButton variant={BUTTON_VARIANTS.SECONDARY}>
                  <Export16 />
                </IconButton>
              </CSVLink>
            </Tooltip>
          ) : (
            <IconButton isDisabled variant={BUTTON_VARIANTS.SECONDARY}>
              <Export16 />
            </IconButton>
          )}
        </TableActions>
      </CampaignAnalyticsCardHeader>
      <TableWrapper>
        {sortedHeatmapRows?.length === 0 && searchInputValue && (
          <EmptyStateWrapper>
            <EmptyState
              title="No results"
              subtitle="Try adjusting your search and try again."
              Illustration={() => <NoResult width={150} height={150} />}
            />
          </EmptyStateWrapper>
        )}
        {sortedHeatmapRows?.length === 0 && !searchInputValue && (
          <EmptyStateWrapper>
            <EmptyState
              title="No clicks to display yet"
              subtitle="We’ll display metrics when recipients start to engage with your campaign."
              Illustration={() => <Overview width={150} height={150} />}
            />
          </EmptyStateWrapper>
        )}
        {sortedHeatmapRows?.length !== 0 && (
          <StyledTable data-test={DATA_TEST_DYNAMIC_HEATMAP_TABLE}>
            <StyledHeader
              data-url-only-view={urlOnlyView}
              data-is-analytics-bot-filtering-enabled={isAnalyticsBotFilteringEnabled}
              columns={SELECTED_COLS}
              sortedBy={heatmapSortedBy}
              setSortedBy={setHeatmapSortedBy}
              sortDirection={heatmapSortDirection}
              setSortDirection={setHeatmapSortDirection}
              isSticky
            />
            <StyledRows
              data-url-only-view={urlOnlyView}
              data-is-analytics-bot-filtering-enabled={isAnalyticsBotFilteringEnabled}
              rows={sortedHeatmapRows}
              columns={SELECTED_COLS}
            />
          </StyledTable>
        )}
      </TableWrapper>
    </StyledCampaignAnalyticsCard>
  );
};
